<template>
  <div class="challenge-page">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderChallenge :page="currentPageInfo" />
    <PostItem
      :post-content="postContent"
      :side-content="sideContent"
    />
    <BlueSection
      v-if="!authenticated"
      :connected="authenticated"
      diagonal="none"
      class="blue-section-item"
    />
    <DoNotMissSection
      v-if="doNotMiss.length"
      :slider-items="doNotMiss"
    />
    <OfferSection
      v-if="offer"
      :offer="offer"
    >
      Acheter ce jeu
    </OfferSection>
    <section
      v-if="otherCommunities?.length"
      class="other-communities section"
    >
      <div class="title-line line">
        <h2 class="title-strat">ces communautés peuvent vous intéresser</h2>
        <NuxtLink
          to="/communautes"
          class="button-more"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 19.328 34.43"
          >
            <path
              d="M18.71 15.72 3.66.62a2.178 2.178 0 0 0-3.041 0 2.178 2.178 0 0 0 0 3.041l13.555 13.555L.619 30.77a2.178 2.178 0 0 0 0 3.041 2.178 2.178 0 0 0 3.041 0l15.05-15.05a2.178 2.178 0 0 0 0-3.041Z"
              fill="#fff"
              fill-rule="evenodd"
            />
          </svg>
          Plus de communautés
        </NuxtLink>
      </div>
      <div class="slider">
        <CommunitySlider
          :communities="otherCommunities"
          :animated="animated"
        />
      </div>
    </section>
    <p class="mention">
      * Récompense provisoire sous réserve de maintenir votre place au
      classement jusqu'à la fin du mois.
    </p>
  </div>
</template>

<script setup lang="ts">
import type { PostContent, SideContent } from '~/types'

definePageMeta({
  middleware: (to) => {
    const loadChallenge = useLoadChallenge()
    return loadChallenge(to)
  },
})

const challengesStore = useChallengesStore()
const { challenge, monthlyChallenges } = storeToRefs(challengesStore)

const slidersStore = useDoNotMissStore()
const { doNotMiss } = storeToRefs(slidersStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Tous les challenges',
      route: '/challenges',
    },
    {
      title: challenge.value?.title,
      route: '',
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: challenge.value?.title_seo,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: challenge.value?.title_seo,
  description: challenge.value?.description_seo,
  keywords: challenge.value?.keywords_seo,
  ogTitle: challenge.value?.title_og,
  ogDescription: challenge.value?.description_og,
  ogImage:
    challenge.value?.image_og?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const currentPageInfo = computed(() => {
  const rawChallenge = unref(challenge)
  return {
    title: rawChallenge?.title,
    reward: rawChallenge?.points,
    image: rawChallenge?.image_desktop?.url,
    imageMobile: rawChallenge?.image_mobile?.url,
    isFinished: rawChallenge?.is_finished,
    userStatus: rawChallenge?.user_status,
  }
})

const route = useRoute()

const slug = computed(() => {
  return route.params.challengeName as string
})

const otherCommunities = computed(() => {
  return challenge.value?.communities?.map((element) => ({
    ...element,
    route: `/communautes/${element.slug}`,
  }))
})

const socialStore = useSocialStore()
const { social } = storeToRefs(socialStore)

const postContent = computed(() => {
  return {
    firstTag: {
      title: challenge.value?.community?.title,
      slug: challenge.value?.community?.slug,
    },
    title: challenge.value?.title,
    description: challenge.value?.description,
    user: challenge.value?.contributor,
    followed: social.value.is_following,
    content: challenge.value?.content,
    tags: challenge.value?.tags,
    likes: social.value.likes_count,
    shares: social.value.shares_count,
    slugType: 'challenge',
    is_liked: social.value.is_liked,
    slug: slug.value,
    comments: social.value.comments,
    commentsCount: social.value.comments_count,
    has_more_comments: social.value.has_more_comments,
  } satisfies PostContent
})

const { offer, contentBlocksType } = useOffer(challenge)

const sideContent = computed(() => {
  const navSide = contentBlocksType.value?.flatMap((contentBlock) => {
    if (!contentBlock?.category?.slug) return []
    if (
      [
        'actualites-communaute',
        'avis-joueurs',
        'avis-medias',
        'le-coin-des-wapers',
        'astuces',
        'wapers',
      ].includes(contentBlock.category.slug)
    ) {
      return contentBlock.category
    }
    return []
  })

  return {
    title: challenge.value?.community?.title,
    type: 'challenge',
    route: `/communautes/${challenge.value?.community?.slug}`,
    nav: navSide,
    cards: monthlyChallenges.value,
    subCategoriesFilters: false,
  } satisfies SideContent
})

const loadChallenge = useLoadChallenge()

const animated = ref(true)

const { authenticated } = useAuthenticated()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadChallenge(route)
    animated.value = false
  }
})
</script>

<style lang="scss" scoped>
.challenge-page {
  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 700px) {
  .challenge-page {
    .other-communities {
      padding: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
